import React from 'react';
import { Trees } from 'lucide-react';
import { explainerSlugKey } from 'src/shared/constants/portfolio';
import { PortfolioData } from 'src/app/portfolio/state/api/portfolioQuery';
import useAppNavigation from 'src/shared/hooks/useAppNavigation';
import useCustomer from 'src/app/customers/hooks/useCustomer';
import { SortOrder } from 'src/shared/types';
import { HabitatInsightsMetricType } from 'src/shared/types/graph/survey';
import PortfolioAreaChart from 'src/shared/components/chart-templates/portfolio-area-chart/PortfolioAreaReChart';
import PortfolioExploreButton from './PortfolioExploreButton';
import SummaryCard from './SummaryCard';

const categoryStatusColorClasses = {
    'High connectivity': 'bg-green-0 text-green-100',
    'Medium connectivity': 'bg-orange-0 text-orange-100',
    'Low connectivity': 'bg-red-0 text-red-100',
};

const ConnectivityChart = ({ portfolioData, baselineYear }: { portfolioData: PortfolioData; baselineYear?: number }) => {
    const connectivity = portfolioData.summary.metrics.find(metric => metric.label === 'Connectivity');
    const navigate = useAppNavigation();
    const { currentCustomerId } = useCustomer();

    if (!connectivity || !connectivity.datum || !connectivity.years) {
        return null;
    }

    const chartData = connectivity.years?.map((year, index) => ({ year, value: connectivity.datum?.[index] || 0 })) || [];
    const lastestValue = chartData[chartData.length - 1].value;

    const isHighConnectivity = lastestValue > 0.75;
    const isMediumConnectivity = lastestValue > 0.25 && lastestValue <= 0.75;

    const category = isHighConnectivity ? 'High connectivity' : isMediumConnectivity ? 'Medium connectivity' : 'Low connectivity';

    const handleExploreClick = () => {
        navigate.toPortfolioSiteExplorer({
            customerId: currentCustomerId,
            selectedMetric: HabitatInsightsMetricType.ALL_CONNECTIVITY,
            sortDirection: SortOrder.ASC,
            view: 'list',
        });
    };

    return (
        <SummaryCard
            dataTestId='portfolio-connectivity-chart'
            containerClasses='gap-3 p-5'
            cardIcon={<Trees className='text-primary' />}
            cardTitle='Connectivity'
            cardSubtitle='Degree to which natural habitats are linked, allowing species movement.'
            titleExplainerSlug={{
                defaultSlug: explainerSlugKey.portfolioConnectivity,
                explainerSlug: `${explainerSlugKey.portfolioConnectivity}-te`,
            }}
            cardBody={
                <PortfolioAreaChart
                    chartData={chartData}
                    baselineYear={baselineYear}
                    category={
                        <div className={`flex gap-2 px-2 py-1 rounded-2xl text-sm ${categoryStatusColorClasses[category]}`}>{category}</div>
                    }
                />
            }
            action={<PortfolioExploreButton sectionName='connectivity' onClick={handleExploreClick} />}
            frameworks={connectivity.frameworks}
        />
    );
};

export default ConnectivityChart;
